import { AudienceType, Cadence, Workflow } from '@/client/services/api/graphql/gql/graphql';

import AdminAssignmentsService from '../api/AdminAssignmentsService';
import { setSubmissionContentType } from '../builder/utils/setSubmissionContentType';
import { useMutation } from '@tanstack/react-query';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

export const useUpdateWorkflowName = () => {
  const { t } = useTranslation();
  const { setToast } = useToastStore();

  interface MutationVariables {
    id: string;
    data: Workflow;
    updatedName: string;
    refetchAction: () => void;
    onClose: () => void;
  }

  const updateWorkflowNameMutation = useMutation({
    mutationFn: ({ id, data, updatedName }: MutationVariables) =>
      AdminAssignmentsService.updateWorkflow(id, {
        audience: data.audience.map((audienceItem) => ({
          dateRange: audienceItem.dateRange,
          days: audienceItem.days,
          id: audienceItem.id,
          matchingCriteria: audienceItem.matchingCriteria,
          type:
            audienceItem.type === AudienceType.PartnerType
              ? AudienceType.PartnerType
              : audienceItem.type === AudienceType.HiredDate
              ? AudienceType.HiredDate
              : audienceItem.segmentationType === 'group'
              ? AudienceType.Group
              : audienceItem.segmentationType === 'user'
              ? AudienceType.User
              : AudienceType.Segment,
        })),
        enabled: data.enabled,
        settings: {
          ...data.settings,
          name: updatedName,
          escalationContacts: data.settings.escalationContacts.map((contact) => contact.id),
          nonReminderMessages: data.settings.nonReminderMessages.map((message) => message.id),
          overdueMessages: data.settings.overdueMessages.map((message) => ({
            cadence: message.cadence as Cadence,
            message: message.message.id,
            totalMessages: message.totalMessages,
          })),
          reminderMessages: data.settings.reminderMessages.map((reminder) => ({
            message: reminder.message.id,
            timeBeforeDue: reminder.timeBeforeDue,
          })),
          responses: data.settings.responses.map((response) => ({
            ...response,
            content: {
              id: response?.content?.id,
              contentType: setSubmissionContentType(response?.content?.contentType),
            },
          })),
          triggerObject: data.settings.triggerObject?.id,
          objectType: data.settings.objectType ?? undefined,
        },
      }),
    onSuccess: (_, { refetchAction, onClose }) => {
      refetchAction();
      onClose();

      setToast({
        show: true,
        status: 'success',
        title: t('admin.assignments.update.success'),
      });
    },
    onError: () => {
      setToast({
        show: true,
        status: 'error',
        title: t('admin.assignments.update.error'),
      });
    },
  });

  return { updateWorkflowNameMutation };
};
